import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import {
	IconPencilOutline18 as Pencil,
	IconPlusOutline18 as Plus,
	IconTrash2Outline18 as Trash2,
	IconUsersOutline18 as Users,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { pro, scale } from "@productlane/lib/src/plans-client"
import {
	Button,
	IconButton,
	Input,
	Loader,
	Switch,
	Textarea,
	Tooltip,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import { AnimateChangeInHeight } from "@/components/animate-change-in-height"
import { toast } from "@/components/toast"
import { UpgradeButton } from "@/components/upgrade-button"
import { analytics } from "@/lib/analytics"
import { captureError } from "@/lib/capture-error"
import { useRequiredPlans } from "@/lib/plans"
import { getProjectIcon } from "@/lib/projects"
import { mutateOk, useAuthData, useZero, useZeroWorkspace } from "@/lib/zero"
import { SortIcon } from "@/screens/app/nav-icons"
import { PublishPortalCard } from "@/screens/settings/publish-portal-card"
import {
	settingsNavGroupClass,
	SettingsNavRow,
} from "@/screens/settings/settings-pages/settings-nav-row"
import { SettingItem } from "@/screens/settings/settings-switches/setting-item"
import {
	SettingsCardInsetContext,
	SettingsContainer,
} from "@/screens/settings/settings-switches/settings-container"
import { CreateChangelogTagDialog } from "./create-changelog-tag-dialog"
import { EditChangelogTagDialog } from "./edit-changelog-tag-dialog"

export const ChangelogSettings = () => {
	const zero = useZero()
	const decodedToken = useAuthData()
	const workspace = useZeroWorkspace()
	const inset = useContext(SettingsCardInsetContext)
	const { passes: hasScalePlan } = useRequiredPlans(scale.andUp)
	const { passes: hasProPlan } = useRequiredPlans(pro.andUp)

	const [allTags] = useQuery(queries.changelogTags())
	const [isCreateTagOpen, setIsCreateTagOpen] = useState(false)
	const [editingTag, setEditingTag] = useState<(typeof allTags)[number] | null>(
		null,
	)

	const [subscribeUrl, setSubscribeUrl] = useState(
		workspace?.subscribeUrl ?? "",
	)
	const [isSubscribeUrlEnabled, setIsSubscribeUrlEnabled] = useState(
		!!workspace?.subscribeUrl,
	)
	const [isValidUrl, setIsValidUrl] = useState(true)
	const saveTimeoutRef = useRef<NodeJS.Timeout>(undefined)
	const [customPrompt, setCustomPrompt] = useState(
		workspace?.changelogCustomPrompt ?? "",
	)
	const promptSaveTimeoutRef = useRef<NodeJS.Timeout>(undefined)

	useEffect(() => {
		if (workspace?.subscribeUrl) {
			setSubscribeUrl(workspace.subscribeUrl)
			setIsSubscribeUrlEnabled(true)
		}
	}, [workspace?.subscribeUrl])

	useEffect(() => {
		if (workspace?.changelogCustomPrompt !== undefined) {
			setCustomPrompt(workspace.changelogCustomPrompt ?? "")
		}
	}, [workspace?.changelogCustomPrompt])

	// URL validation function
	const validateUrl = (url: string): boolean => {
		const trimmedUrl = url.trim()
		if (!trimmedUrl) return true // Empty is valid
		// Reject http:// URLs
		if (trimmedUrl.startsWith("http://")) return false
		try {
			// Add protocol if missing
			const urlWithProtocol = url.startsWith("https://")
				? url
				: `https://${url}`
			new URL(urlWithProtocol)
			return true
		} catch {
			return false
		}
	}

	// Auto-save function with debouncing
	const autoSaveUrl = useCallback(
		(url: string) => {
			if (!workspace || !isSubscribeUrlEnabled) return

			// Clear existing timeout
			if (saveTimeoutRef.current) {
				clearTimeout(saveTimeoutRef.current)
			}

			// Set new timeout for auto-save
			saveTimeoutRef.current = setTimeout(() => {
				const saveUrl = async () => {
					const isValid = validateUrl(url)
					setIsValidUrl(isValid)

					if (isValid && url.trim()) {
						// Add protocol if missing
						const finalUrl =
							url.startsWith("http://") || url.startsWith("https://")
								? url
								: `https://${url}`

						const result = await zero.mutate(
							mutators.workspaces.update({
								id: workspace.id,
								subscribeUrl: finalUrl,
							}),
						).client

						if (result.type === "success") {
							toast.success({
								title: "Email subscription URL saved",
							})

							analytics.track("update-changelog-subscribe-url", {
								url: finalUrl,
							})
						} else {
							console.error("Failed to auto-save subscribe URL:", result.error)
							captureError(result.error, {
								domain: "general",
								action: "save changelog subscribe url",
								workspaceId: workspace.id,
								userId: decodedToken.userId,
							})
							toast.error({
								title: "Failed to save email subscription URL",
							})
						}
					} else if (isValid && !url.trim()) {
						// Save empty string if valid but empty
						const result = await zero.mutate(
							mutators.workspaces.update({
								id: workspace.id,
								subscribeUrl: null,
							}),
						).client

						if (result.type === "success") {
							toast.success({
								title: "Email subscription URL cleared",
							})
						} else {
							console.error("Failed to clear subscribe URL:", result.error)
							captureError(result.error, {
								domain: "general",
								action: "save changelog subscribe url",
								workspaceId: workspace.id,
								userId: decodedToken.userId,
							})
							toast.error({
								title: "Failed to save email subscription URL",
							})
						}
					}
				}
				void saveUrl()
			}, 1000) // 1 second debounce
		},
		[workspace, isSubscribeUrlEnabled, zero, decodedToken],
	)

	const autoSavePrompt = useCallback(
		(value: string) => {
			if (!workspace) return
			if (promptSaveTimeoutRef.current) {
				clearTimeout(promptSaveTimeoutRef.current)
			}
			promptSaveTimeoutRef.current = setTimeout(() => {
				const save = async () => {
					const result = await zero.mutate(
						mutators.workspaces.update({
							id: workspace.id,
							changelogCustomPrompt: value.trim() || null,
						}),
					).client
					if (result.type === "success") {
						toast.success({ title: "Custom prompt saved" })
						analytics.track("update-changelog-custom-prompt")
					} else {
						toast.error({ title: "Failed to save custom prompt" })
					}
				}
				void save()
			}, 1000)
		},
		[workspace, zero],
	)

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (saveTimeoutRef.current) {
				clearTimeout(saveTimeoutRef.current)
			}
			if (promptSaveTimeoutRef.current) {
				clearTimeout(promptSaveTimeoutRef.current)
			}
		}
	}, [])

	if (!workspace) {
		return (
			<div className="flex items-center justify-center">
				<Loader />
			</div>
		)
	}

	return (
		<>
			<PublishPortalCard
				checked={workspace.changelogPublic || false}
				onCheckedChange={async (value) => {
					await mutateOk(
						zero.mutate(
							mutators.workspaces.update({
								id: workspace.id,
								changelogPublic: value,
								changelogAutomaticDrafts: workspace.changelogAutomaticDrafts,
							}),
						),
					)
					toast.success({
						title: `Changelog ${!workspace.changelogPublic ? "is now public" : "is now hidden"}`,
					})
					analytics.track("toggle-changelog-enabled", {
						value,
						enabled: true,
					})
				}}
				title="Changelog"
				description="Customers can access the changelog with this link."
				link="changelog"
			/>

			<div
				className={cn(
					settingsNavGroupClass,
					"my-4 transition-opacity duration-200 ease-in-out",
					// The group bleeds by default; the changelog popover has no inset.
					!inset && "sm:mx-0",
					!workspace.changelogPublic && "opacity-50",
				)}
			>
				<SettingsNavRow
					icon={SortIcon}
					title="Portal settings"
					description="Set up SSO, custom domain, theme and a private portal."
					link="/settings/portal"
				/>
				<SettingsNavRow
					icon={Users}
					title="Multiple portals"
					description="Activate the support portal for individual customer portals."
					link="/settings/support-portal"
				/>
			</div>

			{/* Changelog Tags */}
			<SettingsContainer
				className={cn("mb-4 transition-opacity duration-200 ease-in-out", {
					"opacity-50": !workspace.changelogPublic,
				})}
			>
				<div className="flex items-center justify-between">
					<div className="flex flex-row items-center gap-4">
						<div className="flex flex-1 flex-col gap-1">
							<div className="text-sm font-medium">Changelog labels</div>
							<div className="text-xs font-medium text-secondary">
								Label your changelog posts and let visitors filter by it.
							</div>
						</div>
					</div>
					{hasScalePlan ? (
						<Button
							variant="outline"
							size="sm"
							Icon={Plus}
							onClick={() => setIsCreateTagOpen(true)}
						>
							New label
						</Button>
					) : (
						<UpgradeButton required={scale.andUp} />
					)}
				</div>
				{hasScalePlan && allTags.length > 0 && (
					<div className="mt-4 flex flex-col gap-1 pt-2">
						{allTags.map((tag) => (
							<div
								key={tag.id}
								className="group flex cursor-default items-center justify-between rounded-md px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800/50"
							>
								<div className="flex items-center gap-2">
									{tag.icon ? (
										(() => {
											const Icon = getProjectIcon(tag.icon)
											return (
												<Icon
													className="h-3.5 w-3.5"
													style={{ color: tag.color }}
												/>
											)
										})()
									) : (
										<span
											className="inline-block h-2.5 w-2.5 rounded-full"
											style={{ backgroundColor: tag.color }}
										/>
									)}
									<span className="text-sm">{tag.name}</span>
								</div>
								<div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
									<Tooltip label="Edit label">
										<IconButton
											variant="ghost"
											Icon={Pencil}
											size="sm"
											onClick={() => setEditingTag(tag)}
										/>
									</Tooltip>
									<Tooltip label="Delete label">
										<IconButton
											variant="ghost"
											Icon={Trash2}
											size="sm"
											className="text-red-500 hover:text-red-600"
											onClick={async () => {
												await mutateOk(
													zero.mutate(
														mutators.changelogTags.delete({
															id: tag.id,
														}),
													),
												)
												toast.success({ title: "Label deleted" })
											}}
										/>
									</Tooltip>
								</div>
							</div>
						))}
					</div>
				)}
			</SettingsContainer>

			<CreateChangelogTagDialog
				isOpen={isCreateTagOpen}
				onOpenChange={(open) => setIsCreateTagOpen(open)}
			/>
			<EditChangelogTagDialog
				isOpen={!!editingTag}
				onOpenChange={(open) => {
					if (!open) setEditingTag(null)
				}}
				tag={editingTag}
			/>

			<SettingsContainer
				className={cn("mb-4 transition-opacity duration-200 ease-in-out", {
					"opacity-50": !workspace.changelogPublic,
				})}
			>
				<SettingItem
					title="Email notifications"
					description="Let visitors subscribe and email them when you publish a post."
					action={
						<Switch
							name="changelogEmailNotifications"
							checked={workspace.changelogEmailNotifications}
							onCheckedChange={async (value) => {
								await mutateOk(
									zero.mutate(
										mutators.workspaces.update({
											id: workspace.id,
											changelogEmailNotifications: value,
										}),
									),
								)
								analytics.track("toggle-changelog-email-notifications", {
									value,
								})
							}}
						/>
					}
				/>
				{/* AnimateChangeInHeight sets an explicit height, so padding on it would clip. */}
				<div className="pt-3">
					<AnimateChangeInHeight className="flex flex-col gap-2">
						<div className="flex items-center justify-between pr-1">
							<div className="flex flex-row items-center gap-4">
								<div className="flex flex-1 flex-col gap-1">
									<div className="text-sm font-medium">Email subscription</div>
									<div className="text-xs font-medium text-secondary">
										Add a custom email subscription URL for your changelog.
									</div>
								</div>
							</div>

							<Switch
								name="subscribeUrl"
								label={null}
								checked={isSubscribeUrlEnabled}
								onCheckedChange={async (value) => {
									setIsSubscribeUrlEnabled(value)
									if (!value) {
										// Clear the URL when disabling
										await mutateOk(
											zero.mutate(
												mutators.workspaces.update({
													id: workspace.id,
													subscribeUrl: null,
												}),
											),
										)
										setSubscribeUrl("")
										toast.success({
											title: "Email subscription disabled",
										})
									} else {
										// Auto-save the current URL when enabling
										if (subscribeUrl.trim()) {
											const finalUrl =
												subscribeUrl.startsWith("http://") ||
												subscribeUrl.startsWith("https://")
													? subscribeUrl
													: `https://${subscribeUrl}`
											await mutateOk(
												zero.mutate(
													mutators.workspaces.update({
														id: workspace.id,
														subscribeUrl: finalUrl,
													}),
												),
											)
											toast.success({
												title: "Email subscription enabled and URL saved",
											})
										} else {
											toast.success({
												title: "Email subscription enabled",
											})
										}
									}
									analytics.track("toggle-subscribe-url", {
										value,
									})
								}}
							/>
						</div>
						{isSubscribeUrlEnabled && (
							<div className="mt-4 flex items-end gap-2 pr-1">
								<div className="min-w-0 flex-1">
									<Input
										placeholder="example.com/signup"
										value={subscribeUrl}
										onChange={(e) => {
											const newUrl = e.target.value
											setSubscribeUrl(newUrl)
											// Trigger auto-save
											void autoSaveUrl(newUrl)
										}}
										name="subscribe-url"
										className={cn("mb-1", {
											"border-red-500": !isValidUrl && subscribeUrl.trim(),
										})}
									/>
									{!isValidUrl && subscribeUrl.trim() && (
										<div className="ml-1 mt-1 text-xs text-red-500">
											Invalid URL format.
										</div>
									)}
								</div>
							</div>
						)}
					</AnimateChangeInHeight>
				</div>
			</SettingsContainer>

			<SettingsContainer
				className={cn("transition-opacity duration-200 ease-in-out", {
					"opacity-50": !workspace.changelogPublic,
				})}
			>
				<SettingItem
					title="Release intelligence"
					description="Drafts a Changelog from completed Linear projects, closed issues, and GitHub commits."
					action={
						<>
							{hasProPlan ? (
								<Switch
									name="drafts"
									checked={workspace.changelogAutomaticDrafts}
									onCheckedChange={async (value) => {
										await mutateOk(
											zero.mutate(
												mutators.workspaces.update({
													id: workspace.id,
													changelogPublic: workspace.changelogPublic,
													changelogAutomaticDrafts: value,
												}),
											),
										)

										analytics.track("toggle-changelog-drafts", {
											value,
											enabled: true,
										})
									}}
								/>
							) : (
								<UpgradeButton required={pro.andUp} />
							)}
						</>
					}
				/>

				{workspace.changelogAutomaticDrafts && hasProPlan && (
					<div
						className={cn("pt-2 transition-opacity duration-200 ease-in-out", {
							"opacity-50": !workspace.changelogPublic,
						})}
					>
						<div className="flex flex-col gap-2">
							<div className="text-sm font-medium">Custom prompt</div>
							<div className="text-xs font-medium text-secondary">
								Add custom instructions for AI-generated changelog entries.
							</div>
							<Textarea
								name="changelogCustomPrompt"
								placeholder="e.g., Focus on user-facing changes, use a friendly tone..."
								value={customPrompt}
								onChange={(e) => {
									setCustomPrompt(e.target.value)
									autoSavePrompt(e.target.value)
								}}
								minRows={3}
							/>
						</div>
					</div>
				)}
			</SettingsContainer>
		</>
	)
}
